
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "@/types/loan";
import { LoanPurpose } from "@/services/api/types";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LoanPurposeFieldProps {
  form: UseFormReturn<FormValues>;
  language?: string;
  className?: string;
}

export const LoanPurposeField = ({ form, language = "fi", className }: LoanPurposeFieldProps) => {
  // Get label and options based on language
  const getLabels = () => {
    if (language === "sv") {
      return {
        purpose: "Lånets syfte",
        options: {
          [LoanPurpose.Renovation]: "Renovering",
          [LoanPurpose.FurnitureOrElectronics]: "Möbler/elektronik",
          [LoanPurpose.CarOrBoatOrMotorcycle]: "Bil/båt/motorcykel",
          [LoanPurpose.LivingCosts]: "Levnadskostnader",
          [LoanPurpose.Healthcare]: "Sjukvård",
          [LoanPurpose.Study]: "Studier",
          [LoanPurpose.HobbiesOrPet]: "Hobbyer/husdjur",
          [LoanPurpose.Holiday]: "Semester",
          [LoanPurpose.Wedding]: "Bröllop",
          [LoanPurpose.RepayExistingLoans]: "Betala befintliga lån",
          [LoanPurpose.Taxes]: "Skatter",
          [LoanPurpose.Investments]: "Investeringar",
          [LoanPurpose.LandOrProperty]: "Mark/fastighet",
          [LoanPurpose.Company]: "Företag",
          [LoanPurpose.Other]: "Annat"
        }
      };
    } else if (language === "en") {
      return {
        purpose: "Loan Purpose",
        options: {
          [LoanPurpose.Renovation]: "Renovation",
          [LoanPurpose.FurnitureOrElectronics]: "Furniture/Electronics",
          [LoanPurpose.CarOrBoatOrMotorcycle]: "Car/Boat/Motorcycle",
          [LoanPurpose.LivingCosts]: "Living Costs",
          [LoanPurpose.Healthcare]: "Healthcare",
          [LoanPurpose.Study]: "Study",
          [LoanPurpose.HobbiesOrPet]: "Hobbies/Pet",
          [LoanPurpose.Holiday]: "Holiday",
          [LoanPurpose.Wedding]: "Wedding",
          [LoanPurpose.RepayExistingLoans]: "Repay Existing Loans",
          [LoanPurpose.Taxes]: "Taxes",
          [LoanPurpose.Investments]: "Investments",
          [LoanPurpose.LandOrProperty]: "Land/Property",
          [LoanPurpose.Company]: "Company",
          [LoanPurpose.Other]: "Other"
        }
      };
    } else {
      return {
        purpose: "Lainan käyttötarkoitus",
        options: {
          [LoanPurpose.Renovation]: "Remontointi",
          [LoanPurpose.FurnitureOrElectronics]: "Huonekalut/elektroniikka",
          [LoanPurpose.CarOrBoatOrMotorcycle]: "Auto/vene/moottoripyörä",
          [LoanPurpose.LivingCosts]: "Elinkustannukset",
          [LoanPurpose.Healthcare]: "Terveydenhuolto",
          [LoanPurpose.Study]: "Opiskelu",
          [LoanPurpose.HobbiesOrPet]: "Harrastukset/lemmikki",
          [LoanPurpose.Holiday]: "Loma",
          [LoanPurpose.Wedding]: "Häät",
          [LoanPurpose.RepayExistingLoans]: "Olemassa olevien lainojen maksaminen",
          [LoanPurpose.Taxes]: "Verot",
          [LoanPurpose.Investments]: "Sijoitukset",
          [LoanPurpose.LandOrProperty]: "Maa/kiinteistö",
          [LoanPurpose.Company]: "Yritys",
          [LoanPurpose.Other]: "Muu"
        }
      };
    }
  };

  const labels = getLabels();

  return (
    <FormField
      control={form.control}
      name="loanPurpose"
      render={({ field }) => (
        <FormItem className={`mb-2 md:mb-4 ${className || ''}`}>
          <FormLabel className="text-gray-700 mb-1 block text-xs sm:text-sm">{labels.purpose}</FormLabel>
          <Select 
            onValueChange={field.onChange} 
            defaultValue={field.value}
          >
            <FormControl>
              <SelectTrigger className="w-full border-blue-200 focus:border-blue-400 transition-colors text-xs sm:text-sm h-8 sm:h-10">
                <SelectValue placeholder={labels.purpose} />
              </SelectTrigger>
            </FormControl>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="w-full max-h-[300px] bg-white border border-blue-100 shadow-md z-50"
              align="start"
            >
              <ScrollArea className="max-h-[200px] overflow-y-auto">
                {Object.entries(labels.options).map(([value, label]) => (
                  <SelectItem 
                    key={value} 
                    value={value} 
                    className="bg-white hover:bg-blue-50 text-xs sm:text-sm py-2.5"
                  >
                    {label}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
